/**
 * Shared TypeScript types for the Communication domain.
 *
 * Single source of truth for all announcement/communication display components.
 * Maps 1:1 to InboxItemResource.php on the backend.
 */

// ============================================================================
// Primitive Types
// ============================================================================

export type CommunicationPriority = 'low' | 'normal' | 'high' | 'urgent'

export type AcknowledgmentLevel = 'none' | 'read' | 'accepted' | 'signed'

// ============================================================================
// Data Types
// ============================================================================

export interface CommunicationSender {
  id: string
  name: string
}

export interface CommunicationAttachment {
  url: string
  name: string
  size: number
  mime_type: string
  thumb_url: string | null
  /** Larger preview for lightbox — proxy URL serving the pre-generated `preview` WebP. */
  preview_url?: string | null
  /** UUID of the underlying Spatie media record. */
  uuid?: string
  /** True when the file is a PDF (application/pdf). */
  is_pdf?: boolean
  /** API endpoint to fetch all PDF pages as images. Null for non-PDFs. */
  pdf_pages_url?: string | null
}

// ============================================================================
// PDF Page Types
// ============================================================================

export interface PdfPageInfo {
  page: number
  url: string
}

export interface PdfPagesResponse {
  page_count: number
  pages: PdfPageInfo[]
}

// ============================================================================
// File Interaction Types
// ============================================================================

export type FileInteractionType = 'opened' | 'closed' | 'downloaded' | 'page_viewed'

export interface FileInteractionEvent {
  media_uuid: string
  interaction_type: FileInteractionType
  file_name?: string
  mime_type?: string
  duration_seconds?: number
  page_number?: number
  metadata?: Record<string, unknown>
}

export interface FileInteractionStatus {
  [media_uuid: string]: {
    opened: boolean
    total_duration_seconds: number
    downloaded: boolean
    open_count: number
  }
}

/**
 * Subtype-specific header payload emitted by the Communication envelope
 * when the underlying communication is e.g. a UserNoticeCommunication.
 *
 * Shape maps 1:1 to `UserNoticeCommunication::headerPayload()`.
 */
export interface SubtypeHeaderField {
  /** Translation key under `models.user-notice.fields.*`. */
  label_key: string
  /** Raw value (date, badge code, client name, ...). */
  value: string | number | null
  /** Renderer hint. */
  type: 'text' | 'date' | 'badge' | 'client'
}

export interface SubtypeHeaderPayload {
  /** Enum case value (e.g. `INCIDENT`, `LEAVE`). */
  type: string
  /** Already-translated type label from the backend. */
  type_label: string
  /** Tone hint (`negative`, `warning`, `info`, ...). */
  color: string
  fields: SubtypeHeaderField[]
}

/**
 * Full inbox item shape — maps to InboxItemResource.php.
 * Used by AnnouncementCard, AnnouncementDetail, Inbox, etc.
 */
export interface CommunicationItem {
  id: string
  communication_id: string
  type: string
  title: string
  content: string
  priority: CommunicationPriority
  sender: CommunicationSender | null
  sent_at: string | null
  read_at: string | null
  acknowledged_at: string | null
  requires_acknowledgment: boolean
  requires_signature: boolean
  is_read: boolean
  is_acknowledged: boolean
  is_pending_acknowledgment: boolean
  is_pending_signature: boolean
  acknowledgment_level: AcknowledgmentLevel
  attachments: CommunicationAttachment[]
  has_conversation: boolean
  conversation_id: string | null
  /** Whether a recipient can start/view a conversation (responder targeting configured + canReply). */
  can_start_conversation: boolean
  /** Per-type header payload for UserNotice/other envelope subtypes; null for plain announcements. */
  subtype_header: SubtypeHeaderPayload | null
  created_at: string
  /** Optional: message count for conversation (used by dialog) */
  messages_count?: number
}

/**
 * Simplified shape from punch clock status endpoint.
 * Used by CommunicationGateModal in the employee portal.
 */
export interface CommunicationGateItem {
  id: string
  communication_id: string
  title: string
  content: string
  priority: string | null
  /** When the communication was delivered to this recipient (ISO 8601). */
  sent_at?: string | null
  requires_acknowledgment?: boolean
  requires_signature?: boolean
  attachments?: CommunicationAttachment[]
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function toBooleanValue(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function toPriority(value: unknown): CommunicationPriority {
  return value === 'low' || value === 'normal' || value === 'high' || value === 'urgent'
    ? value
    : 'normal'
}

function toAcknowledgmentLevel(value: unknown): AcknowledgmentLevel {
  return value === 'none' || value === 'read' || value === 'accepted' || value === 'signed'
    ? value
    : 'none'
}

function normalizeSubtypeHeader(value: unknown): SubtypeHeaderPayload | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  const raw = value as Record<string, unknown>
  const fieldsRaw = Array.isArray(raw.fields) ? raw.fields : []

  const fields: SubtypeHeaderField[] = []
  for (const entry of fieldsRaw) {
    if (!entry || typeof entry !== 'object') continue
    const f = entry as Record<string, unknown>
    const labelKey = toStringValue(f.label_key)
    if (!labelKey) continue
    const fieldType = f.type === 'date' || f.type === 'badge' || f.type === 'client' ? f.type : 'text'
    const rawValue = f.value
    const normalizedValue = typeof rawValue === 'string' || typeof rawValue === 'number' ? rawValue : null
    fields.push({ label_key: labelKey, value: normalizedValue, type: fieldType })
  }

  return {
    type: toStringValue(raw.type),
    type_label: toStringValue(raw.type_label),
    color: toStringValue(raw.color, 'info'),
    fields,
  }
}

function normalizeAttachment(value: unknown): CommunicationAttachment {
  const input = (value && typeof value === 'object') ? value as Record<string, unknown> : {}

  const previewUrl = input.preview_url ?? input.previewUrl
  const out: CommunicationAttachment = {
    url: toStringValue(input.url ?? input.original_url),
    name: toStringValue(input.name ?? input.file_name),
    size: typeof input.size === 'number' ? input.size : 0,
    mime_type: toStringValue(input.mime_type ?? input.mimeType),
    thumb_url: typeof input.thumb_url === 'string'
      ? input.thumb_url
      : (typeof input.thumbUrl === 'string' ? input.thumbUrl : null),
  }
  if (typeof previewUrl === 'string' && previewUrl !== '') {
    out.preview_url = previewUrl
  }
  if (typeof input.uuid === 'string' && input.uuid !== '') {
    out.uuid = input.uuid
  }
  if (typeof input.is_pdf === 'boolean') {
    out.is_pdf = input.is_pdf
  }
  if (typeof input.pdf_pages_url === 'string' && input.pdf_pages_url !== '') {
    out.pdf_pages_url = input.pdf_pages_url
  }

  return out
}

export function normalizeCommunicationItem(input: unknown): CommunicationItem | null {
  if (!input || typeof input !== 'object') {
    return null
  }

  const data = input as Record<string, unknown>
  const communicationId = toStringValue(data.communication_id ?? data.id)
  if (!communicationId) {
    return null
  }

  const attachmentsRaw = Array.isArray(data.attachments) ? data.attachments : []

  return {
    id: toStringValue(data.id, communicationId),
    communication_id: communicationId,
    type: toStringValue(data.type, 'announcement'),
    title: toStringValue(data.title),
    content: toStringValue(data.content),
    priority: toPriority(data.priority),
    sender: data.sender && typeof data.sender === 'object'
      ? {
          id: toStringValue((data.sender as Record<string, unknown>).id),
          name: toStringValue((data.sender as Record<string, unknown>).name),
        }
      : null,
    sent_at: typeof data.sent_at === 'string' ? data.sent_at : null,
    read_at: typeof data.read_at === 'string' ? data.read_at : null,
    acknowledged_at: typeof data.acknowledged_at === 'string' ? data.acknowledged_at : null,
    requires_acknowledgment: toBooleanValue(data.requires_acknowledgment, false),
    requires_signature: toBooleanValue(data.requires_signature, false),
    is_read: toBooleanValue(data.is_read, false),
    is_acknowledged: toBooleanValue(data.is_acknowledged, false),
    is_pending_acknowledgment: toBooleanValue(data.is_pending_acknowledgment, false),
    is_pending_signature: toBooleanValue(data.is_pending_signature, false),
    acknowledgment_level: toAcknowledgmentLevel(data.acknowledgment_level),
    attachments: attachmentsRaw.map(normalizeAttachment),
    has_conversation: toBooleanValue(data.has_conversation, false),
    conversation_id: typeof data.conversation_id === 'string' ? data.conversation_id : null,
    can_start_conversation: toBooleanValue(data.can_start_conversation, false),
    subtype_header: normalizeSubtypeHeader(data.subtype_header),
    created_at: toStringValue(data.created_at, new Date().toISOString()),
    messages_count: typeof data.messages_count === 'number' ? data.messages_count : undefined,
  }
}

/**
 * Maps a CommunicationGateItem to a partial CommunicationItem with sensible defaults.
 * Used by the punch clock gate to render AnnouncementDetail.
 */
export function gateItemToCommunicationItem(gate: CommunicationGateItem): CommunicationItem {
  const normalized = normalizeCommunicationItem({
    id: gate.id,
    communication_id: gate.communication_id,
    type: 'announcement',
    title: gate.title,
    content: gate.content,
    priority: gate.priority,
    sender: null,
    sent_at: typeof gate.sent_at === 'string' ? gate.sent_at : null,
    read_at: null,
    acknowledged_at: null,
    requires_acknowledgment: gate.requires_acknowledgment ?? true,
    requires_signature: gate.requires_signature ?? false,
    is_read: false,
    is_acknowledged: false,
    is_pending_acknowledgment: true,
    is_pending_signature: gate.requires_signature ?? false,
    acknowledgment_level: 'none',
    attachments: gate.attachments ?? [],
    has_conversation: false,
    conversation_id: null,
    can_start_conversation: false,
    subtype_header: null,
    created_at: new Date().toISOString(),
  })

  if (!normalized) {
    throw new Error('Invalid communication gate item payload')
  }

  return normalized
}
